import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@xanectech/supabase-client';
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Fingerprint,
  Trash2,
  Key,
  Copy,
} from 'lucide-react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@xanectech/ui/components';
import { useToast } from '@xanectech/ui/lib';
import { createLogger } from '@xanectech/lib';
import {
  validateRecoveryKey,
  generateRecoveryKey,
  encryptMasterKeyWithRecoveryKey,
  getCachedMasterKeyMaterial,
  type EncryptedMasterKey,
} from '@xanectech/encryption';
import { clearUnlockStatus } from '../services/UnlockStatusService';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformAuthenticatorAvailable } from '../services/WebAuthnService';
import { WebAuthnSetupDialog } from './WebAuthnSetupDialog';

const logger = createLogger('Auth - SecuritySettings');

interface SecuritySettingsProps {
  title?: string;
  description?: string;
  enforceRecoveryGuard?: boolean;
}

export function SecuritySettings({
  title = 'Security Settings',
  description = 'Manage biometric authentication and encryption access for your account.',
  enforceRecoveryGuard = false,
}: SecuritySettingsProps) {
  const { user } = useAuth();
  const [showWebAuthnSetup, setShowWebAuthnSetup] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [webAuthnEnabled, setWebAuthnEnabled] = useState(false);
  const [webAuthnAvailable, setWebAuthnAvailable] = useState(false);
  const [hasRecoveryWrappedKey, setHasRecoveryWrappedKey] = useState(false);
  const [activePrimaryDeviceCount, setActivePrimaryDeviceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [recoveryVerified, setRecoveryVerified] = useState(false);
  const [verifyingRecovery, setVerifyingRecovery] = useState(false);
  const [recoveryVerificationError, setRecoveryVerificationError] = useState<
    string | null
  >(null);
  const [acknowledgeResetRisk, setAcknowledgeResetRisk] = useState(false);
  const [showRecoverySetup, setShowRecoverySetup] = useState(false);
  const [recoverySetupWords, setRecoverySetupWords] = useState<string[]>([]);
  const [recoverySetupWrappedKey, setRecoverySetupWrappedKey] =
    useState<EncryptedMasterKey | null>(null);
  const [recoverySetupHash, setRecoverySetupHash] = useState<string | null>(
    null,
  );
  const [recoverySetupGenerating, setRecoverySetupGenerating] = useState(false);
  const [recoverySetupSaving, setRecoverySetupSaving] = useState(false);
  const [copiedRecoveryKey, setCopiedRecoveryKey] = useState(false);
  const { toast } = useToast();

  const handleCopyRecoverySetupKey = async () => {
    if (recoverySetupWords.length === 0) return;
    try {
      await navigator.clipboard.writeText(recoverySetupWords.join(' '));
      setCopiedRecoveryKey(true);
      setTimeout(() => setCopiedRecoveryKey(false), 2000);
    } catch (err) {
      logger.error('Failed to copy recovery key', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const canSafelyResetBiometrics =
    !enforceRecoveryGuard ||
    hasRecoveryWrappedKey ||
    activePrimaryDeviceCount > 1;

  const refreshRecoveryReadiness = useCallback(async (): Promise<boolean> => {
    if (!user?.id || !enforceRecoveryGuard) {
      return true;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        'webauthn-recovery-readiness',
      );

      if (error) {
        throw error;
      }

      const readinessEnvelope = data as {
        readiness?: {
          hasRecoveryWrappedKey?: boolean;
          activePrimaryDeviceCount?: number;
          canSafelyResetBiometrics?: boolean;
        };
      };

      const readiness = readinessEnvelope.readiness;
      if (!readiness) {
        throw new Error('Invalid readiness response payload');
      }

      const hasRecovery = Boolean(readiness.hasRecoveryWrappedKey);
      const primaryCount = Number(readiness.activePrimaryDeviceCount || 0);
      const canReset = Boolean(readiness.canSafelyResetBiometrics);

      setHasRecoveryWrappedKey(hasRecovery);
      setActivePrimaryDeviceCount(primaryCount);

      return canReset;
    } catch (error) {
      logger.error('Failed to refresh recovery readiness', {
        error: error instanceof Error ? error.message : String(error),
      });

      // Fail safe when readiness cannot be verified.
      return false;
    }
  }, [user?.id, enforceRecoveryGuard]);

  useEffect(() => {
    const checkStatus = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      const { data: credentials, error: webAuthnError } = await supabase
        .from('webauthn_credentials')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .limit(1);

      if (!webAuthnError && credentials && credentials.length > 0) {
        setWebAuthnEnabled(true);
      }

      if (enforceRecoveryGuard) {
        await refreshRecoveryReadiness();
      }

      try {
        const available = await isPlatformAuthenticatorAvailable();
        setWebAuthnAvailable(
          typeof available === 'boolean' ? available : false,
        );
      } catch (err) {
        logger.error('WebAuthn availability check failed', {
          error: err instanceof Error ? err.message : String(err),
        });
        setWebAuthnAvailable(false);
      }

      setLoading(false);
    };

    void checkStatus();
  }, [user?.id, enforceRecoveryGuard, refreshRecoveryReadiness]);

  const handleWebAuthnComplete = async () => {
    setShowWebAuthnSetup(false);
    setWebAuthnEnabled(true);
  };

  const openRecoverySetupDialog = async () => {
    if (!user?.id) return;

    setRecoverySetupWords([]);
    setRecoverySetupWrappedKey(null);
    setRecoverySetupHash(null);
    setShowRecoverySetup(true);
    setRecoverySetupGenerating(true);

    try {
      const masterKeyMaterial = getCachedMasterKeyMaterial(user.id);
      if (!masterKeyMaterial) {
        throw new Error(
          'Vault is locked. Unlock your vault before setting up a recovery key.',
        );
      }

      const recovery = await generateRecoveryKey();
      const recoveryPhrase = recovery.words.join(' ');
      const wrappedKey = await encryptMasterKeyWithRecoveryKey(
        masterKeyMaterial,
        recoveryPhrase,
      );

      setRecoverySetupWords(recovery.words);
      setRecoverySetupWrappedKey(wrappedKey);
      setRecoverySetupHash(recovery.hash);
    } catch (err) {
      logger.error('Failed to generate recovery key for setup', {
        error: err instanceof Error ? err.message : String(err),
      });
      toast({
        title: 'Failed to generate recovery key',
        description:
          err instanceof Error
            ? err.message
            : 'Please unlock your vault and try again.',
        variant: 'destructive',
      });
      setShowRecoverySetup(false);
    } finally {
      setRecoverySetupGenerating(false);
    }
  };

  const handleConfirmRecoverySetup = async () => {
    if (!recoverySetupWrappedKey || !recoverySetupHash) return;

    setRecoverySetupSaving(true);
    try {
      const { error } = await supabase.functions.invoke('store-master-key', {
        body: {
          recoveryWrappedKey: recoverySetupWrappedKey,
          recoveryKeyHash: recoverySetupHash,
        },
      });

      if (error) throw error;

      await refreshRecoveryReadiness();

      setShowRecoverySetup(false);
      setRecoverySetupWords([]);
      setRecoverySetupWrappedKey(null);
      setRecoverySetupHash(null);

      toast({
        title: 'Recovery key configured',
        description:
          'Your recovery key has been saved. You can now safely reset biometrics if needed.',
        variant: 'success',
      });
    } catch (err) {
      logger.error('Failed to store recovery key', {
        error: err instanceof Error ? err.message : String(err),
      });
      toast({
        title: 'Failed to save recovery key',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRecoverySetupSaving(false);
    }
  };

  const requiresRecoveryPhraseVerification =
    enforceRecoveryGuard && hasRecoveryWrappedKey;

  const canProceedWithReset =
    canSafelyResetBiometrics &&
    (!enforceRecoveryGuard || acknowledgeResetRisk) &&
    (!requiresRecoveryPhraseVerification || recoveryVerified);

  const handleVerifyRecoveryPhrase = async () => {
    if (!user?.id) return;

    const normalizedPhrase = recoveryPhrase.trim().toLowerCase();
    if (!validateRecoveryKey(normalizedPhrase)) {
      setRecoveryVerified(false);
      setRecoveryVerificationError(
        'Invalid recovery phrase. Verify all 24 words and try again.',
      );
      return;
    }

    setVerifyingRecovery(true);
    setRecoveryVerificationError(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'webauthn-verify-recovery-key',
        {
          body: {
            recoveryPhrase: normalizedPhrase,
          },
        },
      );

      if (error) {
        throw error;
      }

      const verificationEnvelope = data as {
        verified?: boolean;
        reason?: string;
      };

      if (!verificationEnvelope.verified) {
        throw new Error(
          verificationEnvelope.reason ||
            'Recovery phrase does not match this account.',
        );
      }

      setRecoveryVerified(true);
      setRecoveryVerificationError(null);
      toast({
        title: 'Recovery verified',
        description:
          'Recovery phrase verified. You can safely proceed with biometric reset.',
        variant: 'success',
      });
    } catch (error) {
      setRecoveryVerified(false);
      setRecoveryVerificationError(
        error instanceof Error
          ? error.message
          : 'Failed to verify recovery phrase',
      );
    } finally {
      setVerifyingRecovery(false);
    }
  };

  const handleResetBiometrics = async () => {
    if (!user?.id) return;

    if (!canProceedWithReset) {
      toast({
        title: 'Complete safety checks first',
        description:
          'Verify recovery readiness and acknowledge the reset warning before continuing.',
        variant: 'destructive',
      });
      return;
    }

    const readinessAllowsReset = await refreshRecoveryReadiness();
    if (!readinessAllowsReset) {
      toast({
        title: 'Reset blocked for data safety',
        description:
          'Set up and verify a recovery key (or add another primary device) before resetting biometrics.',
        variant: 'destructive',
      });
      return;
    }

    setResetting(true);
    try {
      if (enforceRecoveryGuard) {
        const { data: safeResetData, error: safeResetError } =
          await supabase.functions.invoke('webauthn-safe-reset', {
            body: {
              acknowledgeResetRisk,
              recoveryPhrase: requiresRecoveryPhraseVerification
                ? recoveryPhrase.trim().toLowerCase()
                : undefined,
            },
          });

        if (safeResetError) throw safeResetError;

        const safeResetEnvelope = safeResetData as {
          purgeGracePeriodMinutes?: number;
          purgeAfter?: string;
        };

        const purgeAfterText = safeResetEnvelope.purgeAfter
          ? ` Permanent deletion is scheduled after ${safeResetEnvelope.purgeGracePeriodMinutes || 0} minutes (${safeResetEnvelope.purgeAfter}).`
          : '';

        toast({
          title: 'Biometric authentication reset',
          description: `Biometric access has been revoked and your vault is now locked.${purgeAfterText}`,
          variant: 'success',
        });
      } else {
        const { error: credError } = await supabase
          .from('webauthn_credentials')
          .delete()
          .eq('user_id', user.id);

        if (credError) throw credError;

        const { error: deviceError } = await supabase
          .from('encryption_devices')
          .delete()
          .eq('user_id', user.id);

        if (deviceError) throw deviceError;

        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            preferred_unlock_method: 'password',
            webauthn_enabled: false,
            webauthn_setup_completed_at: null,
          })
          .eq('user_id', user.id);

        if (profileError) throw profileError;

        toast({
          title: 'Biometric authentication reset',
          description: 'All biometric devices have been removed.',
          variant: 'success',
        });
      }

      // Lock the user since they no longer have biometric setup
      await clearUnlockStatus(user.id);

      setWebAuthnEnabled(false);
      setShowResetConfirm(false);
      setRecoveryPhrase('');
      setRecoveryVerified(false);
      setRecoveryVerificationError(null);
      setAcknowledgeResetRisk(false);
    } catch (error) {
      logger.error('Failed to reset biometrics', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: 'Failed to reset biometrics',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setResetting(false);
    }
  };

  return (
    <>
      <Card className='border-security/20 bg-gradient-to-br from-security/5 to-transparent'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-security'>
            <Shield className='h-5 w-5' />
            {title}
          </CardTitle>
          <p className='text-sm text-muted-foreground'>{description}</p>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center justify-between p-3 rounded-lg border border-border/50'>
            <div className='flex items-center gap-3'>
              <Fingerprint
                className={`h-5 w-5 ${
                  webAuthnEnabled ? 'text-success' : 'text-warning'
                }`}
              />
              <div>
                <div className='font-medium text-sm'>
                  Biometric Authentication
                </div>
                <div className='text-xs text-muted-foreground'>
                  {loading
                    ? 'Checking status...'
                    : !webAuthnAvailable
                      ? 'Not available on this device'
                      : webAuthnEnabled
                        ? 'Fingerprint/Face ID configured'
                        : 'Set up passkey authentication'}
                </div>
              </div>
            </div>
            <Badge variant={webAuthnEnabled ? 'default' : 'secondary'}>
              {loading
                ? 'Loading...'
                : webAuthnEnabled
                  ? 'Active'
                  : webAuthnAvailable
                    ? 'Setup Required'
                    : 'Unavailable'}
            </Badge>
          </div>

          <div className='flex items-center justify-between p-3 rounded-lg border border-border/50'>
            <div className='flex items-center gap-3'>
              <CheckCircle className='h-5 w-5 text-success' />
              <div>
                <div className='font-medium text-sm'>End-to-End Encryption</div>
                <div className='text-xs text-muted-foreground'>
                  Encryption is active for secure key handling.
                </div>
              </div>
            </div>
            <Badge variant='default'>Active</Badge>
          </div>

          {enforceRecoveryGuard && (
            <div className='flex items-center justify-between p-3 rounded-lg border border-border/50'>
              <div className='flex items-center gap-3'>
                {hasRecoveryWrappedKey || activePrimaryDeviceCount > 1 ? (
                  <CheckCircle className='h-5 w-5 text-success' />
                ) : (
                  <AlertTriangle className='h-5 w-5 text-warning' />
                )}
                <div>
                  <div className='font-medium text-sm'>Recovery Protection</div>
                  <div className='text-xs text-muted-foreground'>
                    {hasRecoveryWrappedKey
                      ? 'Recovery key fallback is configured.'
                      : activePrimaryDeviceCount > 1
                        ? 'Multiple primary devices provide fallback.'
                        : 'Configure recovery key before resetting biometrics.'}
                  </div>
                </div>
              </div>
              {hasRecoveryWrappedKey || activePrimaryDeviceCount > 1 ? (
                <Badge variant='default'>Ready</Badge>
              ) : (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void openRecoverySetupDialog()}
                  disabled={loading || !user}
                >
                  <Key className='h-3.5 w-3.5 mr-1.5' />
                  Set Up
                </Button>
              )}
            </div>
          )}

          {webAuthnAvailable && !webAuthnEnabled && (
            <Button
              variant='outline'
              className='w-full'
              onClick={() => setShowWebAuthnSetup(true)}
              disabled={!user || loading}
            >
              <Fingerprint className='h-4 w-4 mr-2' />
              {!user ? 'Login Required' : 'Setup Biometric Authentication'}
            </Button>
          )}

          {webAuthnEnabled && (
            <div className='space-y-2'>
              <Button
                variant='destructive'
                size='sm'
                className='w-full'
                onClick={() => setShowResetConfirm(true)}
                disabled={!user || loading || !canSafelyResetBiometrics}
              >
                <Trash2 className='h-4 w-4 mr-2' />
                Reset Biometric Authentication
              </Button>

              {enforceRecoveryGuard && !canSafelyResetBiometrics && (
                <Alert variant='destructive'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertDescription>
                    Reset is disabled to prevent data loss. Configure a recovery
                    key or register an additional primary device first.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {!webAuthnAvailable && !loading && (
            <Alert>
              <AlertTriangle className='h-4 w-4' />
              <AlertDescription>
                This device does not support platform biometrics. You can still
                use supported passkey providers on compatible devices.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <WebAuthnSetupDialog
        open={showWebAuthnSetup}
        onOpenChange={setShowWebAuthnSetup}
        onComplete={handleWebAuthnComplete}
      />

      <Dialog
        open={showRecoverySetup}
        onOpenChange={(open) => {
          if (!open && !recoverySetupSaving) {
            setShowRecoverySetup(false);
            setRecoverySetupWords([]);
            setRecoverySetupWrappedKey(null);
            setRecoverySetupHash(null);
          }
        }}
      >
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Key className='h-5 w-5 text-primary' />
              Set Up Recovery Key
            </DialogTitle>
            <DialogDescription>
              Generate a 24-word recovery phrase to protect your vault data if
              you ever lose biometric access.
            </DialogDescription>
          </DialogHeader>

          {recoverySetupGenerating && (
            <div className='flex items-center justify-center py-12'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary' />
              <p className='ml-3 text-sm text-muted-foreground'>
                Generating recovery key...
              </p>
            </div>
          )}

          {!recoverySetupGenerating && recoverySetupWords.length > 0 && (
            <div className='space-y-4'>
              <Alert className='border-warning/30 bg-warning/5'>
                <AlertTriangle className='h-4 w-4 text-warning' />
                <AlertDescription className='text-xs'>
                  Save this recovery key in a secure offline location. It is
                  required if you lose access to your registered devices.
                </AlertDescription>
              </Alert>

              <div className='rounded-md border bg-muted/40 p-3'>
                <div className='grid grid-cols-2 gap-2 text-xs sm:grid-cols-3'>
                  {recoverySetupWords.map((word, index) => (
                    <div
                      key={`${word}-${index}`}
                      className='rounded border bg-background px-2 py-1 font-mono'
                    >
                      <span className='mr-1 text-muted-foreground'>
                        {index + 1}.
                      </span>
                      {word}
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type='button'
                variant='outline'
                className='w-full'
                onClick={() => void handleCopyRecoverySetupKey()}
              >
                <Copy className='h-4 w-4 mr-2' />
                {copiedRecoveryKey
                  ? 'Copied Recovery Key'
                  : 'Copy Recovery Key'}
              </Button>
            </div>
          )}

          {!recoverySetupGenerating && recoverySetupWords.length > 0 && (
            <DialogFooter className='flex-col sm:flex-row gap-2'>
              <Button
                variant='outline'
                onClick={() => {
                  if (!recoverySetupSaving) {
                    setShowRecoverySetup(false);
                    setRecoverySetupWords([]);
                    setRecoverySetupWrappedKey(null);
                    setRecoverySetupHash(null);
                  }
                }}
                disabled={recoverySetupSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleConfirmRecoverySetup()}
                disabled={recoverySetupSaving}
              >
                {recoverySetupSaving
                  ? 'Saving...'
                  : "I've Saved My Recovery Key"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-destructive'>
              <AlertTriangle className='h-5 w-5' />
              Reset Biometric Authentication
            </DialogTitle>
            <DialogDescription>
              This removes all registered biometric devices and switches your
              unlock preference back to password.
            </DialogDescription>
          </DialogHeader>

          <Alert variant='destructive' className='my-4'>
            <AlertTriangle className='h-4 w-4' />
            <AlertDescription className='text-xs'>
              You will need to authenticate with password until you set up
              biometrics again.
            </AlertDescription>
          </Alert>

          {requiresRecoveryPhraseVerification && (
            <div className='space-y-3 rounded-md border border-border/60 p-3'>
              <p className='text-xs text-muted-foreground'>
                Enter your 24-word recovery phrase to verify data recovery
                access before resetting biometrics.
              </p>
              <textarea
                value={recoveryPhrase}
                onChange={(e) => {
                  setRecoveryPhrase(e.target.value);
                  setRecoveryVerified(false);
                  setRecoveryVerificationError(null);
                }}
                rows={3}
                className='w-full rounded-md border border-border bg-background px-3 py-2 text-sm'
                placeholder='word1 word2 word3 ... word24'
                disabled={resetting || verifyingRecovery}
              />
              <div className='flex items-center justify-between gap-2'>
                <span className='text-xs text-muted-foreground'>
                  {recoveryPhrase.trim().split(/\s+/).filter(Boolean).length} /
                  24 words
                </span>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => void handleVerifyRecoveryPhrase()}
                  disabled={
                    resetting ||
                    verifyingRecovery ||
                    recoveryPhrase.trim().split(/\s+/).filter(Boolean)
                      .length !== 24
                  }
                >
                  {verifyingRecovery
                    ? 'Verifying...'
                    : 'Verify Recovery Phrase'}
                </Button>
              </div>

              {recoveryVerified && (
                <Alert>
                  <CheckCircle className='h-4 w-4' />
                  <AlertDescription className='text-xs'>
                    Recovery phrase verified.
                  </AlertDescription>
                </Alert>
              )}

              {recoveryVerificationError && (
                <Alert variant='destructive'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertDescription className='text-xs'>
                    {recoveryVerificationError}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {enforceRecoveryGuard && (
            <label className='flex items-start gap-2 text-xs'>
              <input
                type='checkbox'
                className='mt-0.5'
                checked={acknowledgeResetRisk}
                onChange={(e) => setAcknowledgeResetRisk(e.target.checked)}
                disabled={resetting}
              />
              <span className='text-muted-foreground'>
                I understand this revokes biometric access on this account and I
                may need recovery or password unlock to regain data access.
              </span>
            </label>
          )}

          <DialogFooter className='flex-col sm:flex-row gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setShowResetConfirm(false);
                setRecoveryPhrase('');
                setRecoveryVerified(false);
                setRecoveryVerificationError(null);
                setAcknowledgeResetRisk(false);
              }}
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleResetBiometrics}
              disabled={resetting || !canProceedWithReset}
            >
              {resetting ? 'Resetting...' : 'Reset Biometrics'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
